import type { ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";
import { A, Slot, cn, rv, type Img } from "@/flash/ui";

/* Editorial building blocks shared by the below-hero sections (tech style only). */

/** 1280px content shell, shared by every section so copy edges line up site-wide */
export function Shell({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("mx-auto w-full max-w-[1280px] px-5 md:px-10", className)}>{children}</div>;
}

/** Rare small section marker, e.g. "Platform" */
export function Kicker({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("sx-kicker", className)}>{children}</span>;
}

/** Headline revealed through a line mask (falls back to visible without JS) */
export function MaskTitle({ as = "h2", children, className }: { as?: "h2" | "h3"; children: ReactNode; className?: string }) {
  const Tag = as;
  return (
    <Tag {...rv()} className={cn("sx-line", className)}>
      <span>{children}</span>
    </Tag>
  );
}

/** Lazy-loaded image (keeps GPU free for the hero cube) filling its parent; parent sets size. Optional mask reveal. */
export function Pic({ img, className, imgClass, sizes, reveal = true }: { img?: Img; className?: string; imgClass?: string; sizes?: string; reveal?: boolean }) {
  return (
    <div {...(reveal ? rv() : {})} className={cn("sx-media overflow-hidden", !/(^|\s)absolute(\s|$)/.test(className || "") && "relative", className)}>
      <Slot img={img} sizes={sizes} className={imgClass} />
    </div>
  );
}

/** Restrained outlined text link-button */
export function GhostLink({ to, children, className }: { to?: string; children: ReactNode; className?: string }) {
  return (
    <A to={to} className={cn("sx-ghost group", className)}>
      {children}
      <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden />
    </A>
  );
}

export const pad2 = (n: number) => String(n).padStart(2, "0");